// Haptics helper backed by the `web-haptics` library.
//
// Why `web-haptics`:
// - Works on iOS (Chrome / Safari / WebKit) via its audio/form-control
//   trick, where bare `navigator.vibrate` is permanently unsupported.
// - Works on Android via `navigator.vibrate` under the hood.
//
// Design goals kept from the previous implementation:
// - Zero-cost on unsupported platforms: a single boolean check after
//   first use.
// - Respects `prefers-reduced-motion`.
// - Throttled presets so continuous gestures (swiping the particle
//   sphere) can't flood the vibrator or drain battery.
// - Same named exports (`hapticTap`, `hapticLight`, ...) so every
//   existing call site keeps working without changes.

import { WebHaptics } from 'web-haptics';

const isBrowser = typeof window !== 'undefined' && typeof navigator !== 'undefined';

let _checked = false;
let _supports = false;
let _engine = null;

const resolveSupport = () => {
  if (_checked) return _supports;
  _checked = true;

  if (!isBrowser) return (_supports = false);

  // Respect reduced-motion users.
  try {
    if (
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      return (_supports = false);
    }
  } catch (_) {
    // matchMedia unavailable; ignore
  }

  // Gate on touch-capable devices to avoid odd behaviour on laptops
  // that happen to expose the Vibration API.
  const hasTouch =
    'ontouchstart' in window ||
    (navigator.maxTouchPoints && navigator.maxTouchPoints > 0);
  if (!hasTouch) return (_supports = false);

  try {
    _engine = new WebHaptics();
  } catch (_) {
    _engine = null;
    return (_supports = false);
  }

  _supports = true;
  return _supports;
};

export const isHapticsSupported = () => resolveSupport();

// Use performance.now() to avoid Date allocation on the hot path.
const nowMs = () =>
  isBrowser && typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now();

let lastFiredAt = 0;

// Internal: fire a WebHaptics preset (or custom pattern), throttled.
const fire = (input, minGapMs = 0) => {
  if (!resolveSupport() || !_engine) return false;
  if (minGapMs > 0) {
    const t = nowMs();
    if (t - lastFiredAt < minGapMs) return false;
    lastFiredAt = t;
  } else {
    lastFiredAt = nowMs();
  }
  try {
    // .trigger() returns a promise; we never await it, and we swallow
    // rejections so a misbehaving browser can never throw into the
    // render loop.
    const p = _engine.trigger(input);
    if (p && typeof p.catch === 'function') p.catch(() => {});
    return true;
  } catch (_) {
    return false;
  }
};

// Public presets — names preserved for compatibility.
export const hapticTap = () => fire('selection');
export const hapticLight = () => fire('light');
export const hapticMedium = () => fire('medium');
export const hapticSuccess = () => fire('success');

// Throttled pulse for continuous gestures (swiping through the sphere).
// iOS fires haptics via a programmatic click on a native <input switch>,
// and WebKit rate-limits rapid programmatic switch clicks heavily.
// 180 ms default -> ~5 pulses/sec, which is slow enough that iOS
// actually surfaces each Taptic, and uses `medium` which is noticeably
// stronger than `selection`/`light`.
export const hapticPulse = (minGapMs = 180) => fire('medium', minGapMs);

// Escape hatch for custom patterns (see web-haptics docs).
export const vibrate = (pattern, minGapMs = 0) => fire(pattern, minGapMs);
